import { type RefObject, useEffect, useRef } from "react";

/**
 * Brings the not-found cloud's face to life: it drifts toward the pointer, and
 * blinks every few seconds.
 *
 * The eyes are squint chevrons with no pupils to move, so the whole face
 * translates instead, which reads as the cloud turning to look at you.
 *
 * A near-copy of the SEO renderer's `not-found/cloud-eyes.ts`. The two surfaces
 * share no workspace package, so this is a deliberate duplicate rather than an
 * import; keep the constants in step if either is retuned.
 */

/**
 * Furthest the face travels from centre, in viewBox units, per axis.
 *
 * Geometric limits rather than taste: the face has to stay inside the cloud
 * outline at all four diagonal extremes, and the `<svg>` clips anything past its
 * 164x132 viewBox. The mouth's gap to the flat bottom edge makes the vertical
 * axis the tighter of the two. Measured headroom is x 19 / y 13 with a 2-unit
 * margin, so these sit just inside it.
 */
const MAX_OFFSET_X = 18;
const MAX_OFFSET_Y = 12;

/**
 * Pointer distance, as a multiple of the illustration's own size, at which the
 * face reaches full travel. Lower feels more reactive; too low and the face sits
 * pinned at a corner for most of the page.
 */
const REACH = 1.5;

/** How long the eyes stay shut. */
const BLINK_MS = 130;

/** Bounds on the random gap between blinks. Irregular reads as alive. */
const BLINK_GAP_MIN_MS = 2600;
const BLINK_GAP_MAX_MS = 7200;

export const BLINKING_CLASS = "is-blinking";

const clamp = (value: number, limit: number): number => Math.max(-limit, Math.min(limit, value));

type CloudFaceRefs = {
  svgRef: RefObject<SVGSVGElement>;
  faceRef: RefObject<SVGGElement>;
};

export function useCloudFace(): CloudFaceRefs {
  const svgRef = useRef<SVGSVGElement>(null);
  const faceRef = useRef<SVGGElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    const face = faceRef.current;
    if (!svg || !face) return;

    // Both effects are decoration, so neither runs for a reduced-motion viewer.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const onPointerMove = (event: PointerEvent) => {
      const box = svg.getBoundingClientRect();
      // Zero-sized before layout, and dividing by it would yield NaN.
      if (box.width === 0 || box.height === 0) return;

      const x = clamp((event.clientX - (box.left + box.width / 2)) / (box.width * REACH), 1);
      const y = clamp((event.clientY - (box.top + box.height / 2)) / (box.height * REACH), 1);

      face.style.transform = `translate(${(x * MAX_OFFSET_X).toFixed(2)}px, ${(y * MAX_OFFSET_Y).toFixed(2)}px)`;
    };

    // Pointer gone (left the window, or lifted on touch): settle back to centre
    // rather than holding whatever the last position was.
    const onPointerOut = () => {
      face.style.transform = "";
    };

    let shutTimer: ReturnType<typeof setTimeout> | undefined;
    let nextTimer: ReturnType<typeof setTimeout> | undefined;

    // Takes the element as a parameter rather than closing over the narrowed
    // `svg` const, which a hoisted `function` would widen back to nullable.
    const startBlinking = (el: SVGSVGElement) => {
      const scheduleNext = () => {
        nextTimer = setTimeout(
          blink,
          BLINK_GAP_MIN_MS + Math.random() * (BLINK_GAP_MAX_MS - BLINK_GAP_MIN_MS),
        );
      };

      function blink() {
        // A hidden tab shouldn't animate. Skip this one but keep the cycle
        // running, so the eyes are already blinking when the viewer returns.
        if (document.hidden) {
          scheduleNext();
          return;
        }
        el.classList.add(BLINKING_CLASS);
        shutTimer = setTimeout(() => {
          el.classList.remove(BLINKING_CLASS);
          scheduleNext();
        }, BLINK_MS);
      }

      scheduleNext();
    };

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerout", onPointerOut, { passive: true });
    startBlinking(svg);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerout", onPointerOut);
      clearTimeout(shutTimer);
      clearTimeout(nextTimer);
      svg.classList.remove(BLINKING_CLASS);
    };
  }, []);

  return { svgRef, faceRef };
}
